import * as THREE from 'three'
import type { IPoint2 } from '@/plugins/cad/module/common/interface'
import { CaptureType } from './Capturer'

// 捕捉标记（捕捉点的视觉表示）
enum CaptureMarkerType {
  square = 'square', // 正方形
  triangle = 'triangle', // 三角形
  circle = 'circle', // 圆形
  cross = 'cross', // 交叉线
}

const captureTypeToMarkerType: Record<CaptureType, CaptureMarkerType> = {
  [CaptureType.Endpoint]: CaptureMarkerType.square,
  [CaptureType.Node]: CaptureMarkerType.square,
  [CaptureType.Midpoint]: CaptureMarkerType.triangle,
  [CaptureType.Center]: CaptureMarkerType.circle,
  [CaptureType.Nearest]: CaptureMarkerType.cross,
  [CaptureType.None]: CaptureMarkerType.square, // 默认类型，实际不会显示
}

/**
 * 捕捉标记点渲染对象，在屏幕上叠加显示 CAD 捕捉图标。
 */
export class CaptureMarker {
  private mesh: THREE.Points
  private geometry: THREE.BufferGeometry
  private material: THREE.PointsMaterial

  constructor() {
    this.geometry = new THREE.BufferGeometry()
    this.geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(3), 3))

    // 创建一个包含所有图标的雪碧图或动态 Canvas
    this.material = new THREE.PointsMaterial({
      size: 24, // 屏幕像素大小
      sizeAttenuation: false, // 关键：禁用尺寸随距离衰减
      transparent: true,
      depthTest: false, // 永远置顶
      depthWrite: false,
      map: null, // 稍后动态更新
    })

    this.mesh = new THREE.Points(this.geometry, this.material)
    this.mesh.visible = false
    this.mesh.renderOrder = 9999 // 渲染顺序最高
    this.mesh.frustumCulled = false // 禁止视锥体裁剪，确保总是渲染
  }

  /**
   * 将标记点挂载到指定 Three.js 组。
   *
   * @param group - 作为父节点的 Three.js 组
   */
  addToGroup(group: THREE.Group) {
    group.add(this.mesh)
  }

  /**
   * 判断标记点是否已挂载到某个组。
   *
   * @returns 已挂载返回 true，否则返回 false
   */
  isAddedToGroup(): boolean {
    return !!this.mesh.parent
  }

  /** 从父节点移除并释放占用的 GPU 资源 */
  dispose() {
    this.mesh.parent?.remove(this.mesh)
    this.geometry.dispose()
    this.material.dispose()
  }

  /**
   * 更新捕捉图标的位置和样式。
   *
   * @param point - 标记所在的屏幕/世界坐标
   * @param type - 捕捉类型，决定显示的图标形状；None 时隐藏
   */
  update(point: IPoint2, type: CaptureType) {
    if (type === CaptureType.None) {
      this.mesh.visible = false
      return
    }

    // 1. 更新位置
    const posAttr = this.geometry.attributes.position as THREE.BufferAttribute
    posAttr.setXYZ(0, point.x, point.y, 0)
    posAttr.needsUpdate = true

    // 2. 根据类型绘制 Canvas 贴图 (也可以预渲染好几张贴图切换)
    const markerType = captureTypeToMarkerType[type]
    if (!this.material.map || this.material.map.name !== `captureMarker_${markerType}`) {
      this.material.map?.dispose() // 释放旧贴图
      this.material.map = this.createGlyphTexture(markerType)
    }
    this.mesh.visible = true
  }

  private createGlyphTexture(markerType: CaptureMarkerType): THREE.CanvasTexture {
    const size = 64
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')!

    ctx.strokeStyle = '#00ff00' // CAD 经典的捕捉绿
    ctx.lineWidth = 4
    const center = size / 2
    const offset = size / 4

    ctx.clearRect(0, 0, size, size)
    ctx.beginPath()

    switch (markerType) {
      case CaptureMarkerType.square: // 正方形
        ctx.strokeRect(center - offset, center - offset, offset * 2, offset * 2)
        break
      case CaptureMarkerType.triangle: // 三角形
        ctx.moveTo(center, center - offset)
        ctx.lineTo(center - offset, center + offset)
        ctx.lineTo(center + offset, center + offset)
        ctx.closePath()
        ctx.stroke()
        break
      case CaptureMarkerType.circle: // 圆形
        ctx.arc(center, center, offset, 0, Math.PI * 2)
        ctx.stroke()
        break
      case CaptureMarkerType.cross: // 交叉线 (砂漏状)
        ctx.moveTo(center - offset, center - offset)
        ctx.lineTo(center + offset, center + offset)
        ctx.moveTo(center + offset, center - offset)
        ctx.lineTo(center - offset, center + offset)
        ctx.stroke()
        break
    }

    const texture = new THREE.CanvasTexture(canvas)
    texture.needsUpdate = true
    texture.name = `captureMarker_${markerType}`
    return texture
  }
}
